import {
  PredicateNodeBinaryOperators,
  PredicateNodeOperandSubtype,
  PredicateNodeType
} from './predicate.constant';
import {
  IAbstractPredicateNode,
  IAbstractPredicateNodeData,
  IPredicateNodeOperatorInfo,
  PredicateNodeSubtype
} from './predicate.types';

export abstract class AbstractPredicateNode<
  T extends number = PredicateNodeSubtype
> implements IAbstractPredicateNode<T> {
  public order = 1;
  public type: PredicateNodeType;
  public subtype: T;
  public constant: string | null = null;

  constructor(data: IAbstractPredicateNodeData) {
    if (data.order) {
      this.order = data.order;
    }
    this.type = data.type!;
    this.subtype = (data.subtype ?? PredicateNodeBinaryOperators.Equals) as T;
    if (data.constant) {
      this.constant = data.constant;
    }
  }

  isOperand(): boolean {
    return this.type === PredicateNodeType.Operand;
  }

  isOperator(): boolean {
    return this.type === PredicateNodeType.Operator;
  }

  isStartBracket(): boolean {
    return (
      this.type === PredicateNodeType.Operator &&
      this.subtype === PredicateNodeBinaryOperators.StartBracket
    );
  }

  isCloseBracket(): boolean {
    return (
      this.type === PredicateNodeType.Operator &&
      this.subtype === PredicateNodeBinaryOperators.CloseBracket
    );
  }

  isBracket(): boolean {
    return this.isStartBracket() || this.isCloseBracket();
  }

  protected toOperandString() {
    const hashOperandValue: Record<PredicateNodeOperandSubtype, string> = {
      [PredicateNodeOperandSubtype.Constant]: `${this.constant}`,
      [PredicateNodeOperandSubtype.Variable]: ''
    };

    return (
      hashOperandValue[this.subtype as PredicateNodeOperandSubtype] ||
      `Unknown(${this.subtype})`
    );
  }

  protected toOperatorString() {
    switch (this.subtype) {
      case PredicateNodeBinaryOperators.StartBracket:
        return '(';
      case PredicateNodeBinaryOperators.CloseBracket:
        return ')';
      case PredicateNodeBinaryOperators.Equals:
        return '==';
      case PredicateNodeBinaryOperators.NotEqual:
        return '!=';
      case PredicateNodeBinaryOperators.GreaterThan:
        return '>';
      case PredicateNodeBinaryOperators.GreaterThanEqual:
        return '>=';
      case PredicateNodeBinaryOperators.LessThan:
        return '<';
      case PredicateNodeBinaryOperators.LessThanEqual:
        return '<=';
      case PredicateNodeBinaryOperators.And:
        return '&&';
      case PredicateNodeBinaryOperators.Or:
        return '||';
      case PredicateNodeBinaryOperators.Not:
        return '!';
      default:
        return `Unknown(${this.subtype})`;
    }
  }
}

//  Abstract Predicate Node Classes
export abstract class AbstractPredicateNodeOperatorInfo<
  T extends PredicateNodeSubtype
> implements IPredicateNodeOperatorInfo<T> {
  readonly value: T;
  readonly display_name: string;
  readonly icon: string;

  constructor(data: IPredicateNodeOperatorInfo<T>) {
    this.value = data.value;
    this.display_name = data.display_name;
    this.icon = data.icon ?? '';
  }
}
